import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Database, Upload } from "lucide-react";
import ImportMasterDialog from "@/components/ImportMasterDialog";
import DpjpBelumTerverifikasi from "@/components/DpjpBelumTerverifikasi";

// Not a master list of its own: DPJP typed with "Lainnya (isi manual)" on /ajukan, waiting to be mapped.
const VERIFIKASI_TAB = "dpjp_verifikasi";
const TABS = [
  { key: "ksm", label: "Departemen", desc: "Departemen / Kelompok Staf Medis (KSM)" },
  { key: "dpjp", label: "DPJP", desc: "Dokter Penanggung Jawab Pelayanan per Departemen (pilihan DPJP di form pengajuan /ajukan)" },
  { key: "bangsal", label: "Bangsal/Kategori", desc: "Bangsal atau kategori perawatan" },
];

export default function MasterDataPage() {
  const [tab, setTab] = useState("ksm");
  const [items, setItems] = useState([]);
  const [ksmList, setKsmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [nama, setNama] = useState("");
  const [ksmId, setKsmId] = useState("");
  const [aktif, setAktif] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const fetchKsm = useCallback(() => {
    api.get("/master/ksm", { params: { semua: true } }).then((r) => setKsmList(r.data)).catch(() => {});
  }, []);

  const fetchItems = useCallback(async () => {
    if (!TABS.some((t) => t.key === tab)) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/master/${tab}`, { params: { semua: true } });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchKsm(); }, [fetchKsm]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => {
    setEditItem(null);
    setNama("");
    setKsmId("");
    setAktif(true);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setNama(item.nama);
    setKsmId(item.ksm_id || "");
    setAktif(item.aktif !== false);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!nama.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    if (tab === "dpjp" && !ksmId) {
      toast.error("Pilih Departemen untuk DPJP ini");
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        const payload = { nama: nama.trim(), aktif };
        if (tab === "dpjp") payload.ksm_id = ksmId;
        await api.put(`/master/${tab}/${editItem.id}`, payload);
        toast.success("Data berhasil diperbarui");
      } else {
        const payload = { nama: nama.trim() };
        if (tab === "dpjp") payload.ksm_id = ksmId;
        await api.post(`/master/${tab}`, payload);
        toast.success("Data berhasil ditambahkan");
      }
      setDialogOpen(false);
      fetchItems();
      if (tab === "ksm") fetchKsm();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleAktif = async (item) => {
    try {
      await api.put(`/master/${tab}/${item.id}`, { aktif: !(item.aktif !== false) });
      toast.success(`"${item.nama}" ${item.aktif !== false ? "dinonaktifkan" : "diaktifkan"}`);
      fetchItems();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const activeTab = TABS.find((t) => t.key === tab) || TABS[1];

  return (
    <div className="space-y-5" data-testid="master-data-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Master Data</h1>
          <p className="text-sm text-slate-500 mt-1">
            Data standar terpusat — mencegah duplikasi dan typo pada input pasien.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="import-master-button">
          <Upload size={14} /> Import Excel
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-slate-200">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}
              className="data-[state=active]:bg-[#00B9AD] data-[state=active]:text-white">
              {t.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value={VERIFIKASI_TAB} data-testid={`tab-${VERIFIKASI_TAB}`}
            className="data-[state=active]:bg-[#00B9AD] data-[state=active]:text-white">
            DPJP belum terverifikasi
          </TabsTrigger>
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Database size={14} className="text-[#60C0D0]" /> {t.desc} — {items.length} entri
              </p>
              <Button size="sm" className="gap-1.5 bg-[#00B9AD] hover:bg-[#009E94] text-white"
                onClick={openAdd} data-testid={`add-${t.key}-button`}>
                <Plus size={14} /> Tambah {t.label}
              </Button>
            </div>
            <Card className="border-slate-200/80 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`table-${t.key}`}>
                  <thead className="bg-[#267B84] text-white">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold">Nama</th>
                      {t.key === "dpjp" && <th className="text-left px-4 py-3 text-xs font-semibold">Departemen</th>}
                      {t.key !== "bangsal" && <th className="text-left px-4 py-3 text-xs font-semibold">Sub-spesialisasi</th>}
                      <th className="text-left px-4 py-3 text-xs font-semibold">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {loading ? (
                      <tr><td colSpan={4} className="px-4 py-14 text-center text-slate-400">Memuat data...</td></tr>
                    ) : items.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-14 text-center text-slate-400">
                        Belum ada data. Klik "Tambah {t.label}" untuk membuat entri pertama.
                      </td></tr>
                    ) : items.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50" data-testid={`${t.key}-row-${item.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{item.nama}</td>
                        {t.key === "dpjp" && <td className="px-4 py-3 text-slate-600">{item.ksm_nama}</td>}
                        {t.key !== "bangsal" && <td className="px-4 py-3 text-slate-500 text-xs">{item.sub_spesialisasi || "—"}</td>}
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full border text-xs font-medium ${
                            item.aktif !== false
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                            {item.aktif !== false ? "Aktif" : "Nonaktif"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" className="h-8 gap-1 text-xs"
                              onClick={() => openEdit(item)} data-testid={`edit-${t.key}-${item.id}`}>
                              <Pencil size={13} /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs"
                              onClick={() => toggleAktif(item)} data-testid={`toggle-${t.key}-${item.id}`}>
                              {item.aktif !== false ? "Nonaktifkan" : "Aktifkan"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        ))}
        <TabsContent value={VERIFIKASI_TAB} className="mt-4">
          {tab === VERIFIKASI_TAB && <DpjpBelumTerverifikasi />}
        </TabsContent>
      </Tabs>

      <ImportMasterDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => { fetchItems(); fetchKsm(); }}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="master-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editItem ? "Edit" : "Tambah"} {activeTab.label}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Nama harus unik (tidak peka huruf besar/kecil) untuk mencegah duplikasi.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Nama <span className="text-red-500">*</span></Label>
              <Input value={nama} onChange={(e) => setNama(e.target.value)} required
                placeholder={`Nama ${activeTab.label}...`} data-testid="master-nama-input" />
            </div>
            {tab === "dpjp" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Departemen <span className="text-red-500">*</span></Label>
                <Select value={ksmId} onValueChange={setKsmId}>
                  <SelectTrigger data-testid="master-ksm-select"><SelectValue placeholder="Pilih KSM" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {ksmList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editItem && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                <Label className="text-sm text-slate-700">Status Aktif</Label>
                <Switch checked={aktif} onCheckedChange={setAktif} data-testid="master-aktif-switch" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="master-cancel-button">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="master-submit-button"
                className="bg-[#00B9AD] hover:bg-[#009E94] text-white">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
